<template>
  <div>
    <PageHeader />
    <ProjInfo :projectInfo="projectInfo" />


    <v-container class="section-block">
        <h1>Keys</h1>
        <br><br>
        <v-row>
            <v-col class="col-12 col-md-4 col-sm-6">
                <p>
                    Keyframes in Ae relate to ENG specs through a series of attributes. 
                </p>
                <ul>
                    <li>Ease</li>
                    <li>Duration</li>
                    <li>Delay</li>
                </ul>

                <p>
                    Spectrum comes with two token libraries: <b>SumUX</b> and <b>Material</b> 
                    –switchable at the top of the screen. Additionally, you may switch between milliseconds and frames (at 60fps)
                    to work with the most useful timebase.
                </p>
                <br>
                <h2>Usage</h2>
                <ul>
                    <li>Select pairs of keyframes</li>
                    <li>Click the [ → ] within each attribute block to apply a single attribute</li>
                    <li> -  -  - Orrrr</li>
                    <!-- <li>Enable/disable different attribute blocks</li> -->
                    <li>Click <b>Apply All</b> to set groups of attributes to selected keys</li>
                </ul>
            </v-col>
            <v-col class="col-12 col-md-8 col-sm-6">
                <video width="100%" :src="video.keys" :poster="images.posterKeys" controls />
                <!-- <img src="/images/Spectrum-keys.jpg" width="100%" alt=""> -->
            </v-col>
            </v-row>
            <v-row>
                <v-col class="col-12 col-md-3">
                    <h3>Ease</h3>
                    <p>
                        Inspired by <a href="https://aescripts.com/flow/" target="_blank">Flow</a>, a series of easing tokens 
                        is available for the selected library. 
                    </p>
                    <p>
                        Easing is accurate for all property types, including non-uniform scaling and paths. 
                    </p>
                </v-col>
                <v-col class="col-12 col-md-3">
                    <h3>Duration</h3>
                    <p>
                        The spacing between selected keyframe pairs, or the time between the first and second keyframe.
                    </p>
                    <p>
                        Stepping through different Duration Tokens will affect selected keyframes.
                    </p>
                </v-col>
                <v-col class="col-12 col-md-3">
                    <h3>Delay</h3>
                    <p>
                        The time between the playead and the first keyframe.
                    </p>
                    <p>
                        Stepping through different Delay Tokens will affect selected keyframes.
                    </p>
                </v-col>
                <v-col class="col-12 col-md-3">
                    <h3>Stagger</h3>
                    <p>
                        Original to <b>Spectrum</b>, Stagger allows selected groups of keyframes to be offset in time 
                        for each layer. 
                    </p>
                    <p>
                        Stepping through different Stagger Tokens will affect selected keyframes.
                    </p>
                </v-col>
            </v-row>


        <h1>Patterns</h1>
        <br><br>
        <v-row>
            <v-col class="col-12 col-md-4 col-sm-6">
                <p>
                    Designs continue to expand across many screen sizes while keframes remain 
                    locked to absolute coordinates. ENG has long used percentages (50% of the screen width, 
                    100% of the box height, etc) to define motion, and now so can we.
                 </p>
                <p>
                    Patterns may be thought of as <i>responsive presets</i>. 
                    They combine the idea of <b>keyframe attributes</b> with something entirely new:
                </p>
                <h2>
                    Relative transforms
                </h2>
                <p>
                    Selecing a pattern will reveal a set of properties. Available transforms are:
                </p>
                <ul>
                    <li>Position X</li>
                    <li>Position Y</li>
                    <li>Scale</li>
                    <li>Opacity</li>
                    <li>Rotation</li>
                </ul>
                <p>
                    Each transform is built on percentages making the motion relative to the shape or the screen size. 
                    To play with percentages, select a pattern and begin editing.
                </p>
            </v-col>
            <v-col class="col-12 col-md-8 col-sm-6">
                <video width="100%" :src="video.patterns" :poster="images.posterPatterns" controls />
                <!-- <img src="/images/Spectrum-pattern.jpg" width="100%" alt=""> -->
            </v-col>
            </v-row>

            <v-row>
                <v-col class="col-12 col-md-4">
                    <h3>Pattern viewer</h3>
                    <p>
                        Selecting a pattern will open its property details to view the details of a pattern.
                    </p>
                </v-col>
                <v-col class="col-12 col-md-4">
                    <h3>Create New Keys →</h3>
                    <p>
                        Click to apply the selected pattern to the selected layers and create new keyframes based on 
                        the enabled properties in the pattern.
                    </p>
                </v-col>
                <v-col class="col-12 col-md-4">
                    <h3>Stagger</h3>
                    <p>
                        For wach selected layer the selected pattern will be applied to it. It is also possible to stagger 
                        these new keyframes and offset their time by this value for each successive layer.
                    </p>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <h2>Pattern editor</h2>
                    <p>
                        Click <b>Edit Pattern</b> to begin editing the selected pattern.
                    </p>
                    <p>
                        Each patterns is built on one or all of the 5 available transform properties.
                        These properties will each have an <b>Ease</b>, <b>Duration</b>, and <b>Delay</b>.
                    </p>
                    <p>
                        They will also have <b>Direction</b>, <b>Percentage</b>, and <b>Relation</b> options specific to the property.
                    </p>
                    <h2>Relative transforms</h2>
                    <p>
                        Each property will either increase or decrease on it's own axis by a percentage. 
                        This is handled differently for each property type. 
                    </p>
                </v-col>
                <v-col class="col-12 col-md-8 col-sm-6">
                    <img :src="images.patternEdit" width="100%" alt="">
                </v-col>
            </v-row>
            <v-row>
                <v-col class="col-12 col-md-6">
                    <h3>Into/out of current</h3>
                    <p>
                        A new concept designed to explore what it could mean to use the current value as an anchor for comes before or after.

                    </p>
                    <p>
                        <i>Example:</i>  if you know where a box should land after fading in and sliding up, the first position keyframe
                        has less importance than the second. This gives you the opportunity to adjust the start position more fluidly to 
                        find the correct feel for the transition.
                    </p>
                    <p>
                        Think more about the rate of change to get into or out of the current value and have fun finding the 
                        right percentages. Emphasize the present momement.
                    </p>
                </v-col>
                <v-col class="col-12 col-md-4">
                    <h3>Layer/Comp size</h3>
                    <p>
                        For <b>position</b> changes, an additional option must be selected to use the layer or comp size 
                        as the percentage base.
                    </p>
                    <p>
                        <b>Move Left 50%</b> could mean 50px for a 100px wide box, or 960px for a 1920px wide comp.
                        Select the appropriate relation for the element.
                    </p>
                </v-col>
            </v-row>
    </v-container>

        <!-- <InstallInfo id="install" :projectInfo="projectInfo"/> -->
        <InstallInfo id="install" name="Spectrum" extension :hosts="['After Effects']"/>
    
  </div>
</template>

<script>
// @ is an alias to /src

export default {
  name: 'conclave',
  components: {
  },
  data: () => ({
      images: {
            posterKeys: require('../assets/images/SpectrumPoster - Keys.jpg'),
            posterPatterns: require('../assets/images/SpectrumPoster - Patterns.jpg'),
            patternEdit: require('../assets/images/Spectrum-pattern-edit.jpg'),
        },
        video: {
            keys: require('../assets/video/Spectrum - Keys.mp4'),
            patterns: require('../assets/video/Spectrum - Patterns.mp4'),
        },
      projectInfo: {
          name: 'Spectrum',
          subtitle: 'Motion design with patterns',
          download: 'http://go/spectrum-download',
          updated: 'Apr 19, 2021',
          version: '0.4.6',
          about: `This is an exploration into a new way of animating in Ae. As screens become more complex, it might be time to think about relative motion rather than keyframes. 
          Still a work in progress but we want your feedback.`
      },
  }),
}
</script>